import { omit, pick, uniqueId } from 'lodash';

import { generateWorkflowId } from 'dashboard/lib/stubs/workflow';
import { generateWorkflowVersionId } from 'dashboard/lib/stubs/workflow-version';
import {
  Account,
  AccountAttributes,
  AccountEvent,
  AccountEventAttributes,
  AccountRelationships,
} from 'dashboard/models/account';
import {
  AccountAction,
  AccountActionAttributes,
  AccountActionRelationships,
} from 'dashboard/models/account-action';
import { AccountStatus } from 'dashboard/models/account-status';
import { WorkflowStatus } from 'dashboard/models/workflow';
import { FieldType } from 'lib/fields';
import { ApiModel } from 'lib/interfaces/api';

import { generateAccountTypeId } from './account-type';

export const generateAccountId = () => uniqueId('act_');

export function createMockAccount(
  attributes?: Partial<AccountAttributes>,
  relationships?: Partial<AccountRelationships>,
  id?: string,
  accountStatusId?: string,
  accountTypeId?: string,
): Account {
  return {
    id: id || generateAccountId(),
    type: ApiModel.Account,
    attributes: {
      'created-at': '2019-10-19T06:31:58.000Z',
      'updated-at': '2019-10-19T09:31:58.000Z',
      'reference-id': 'sw:ep_1',
      'name-full': 'Jar Jar Binks',
      'selfie-photo-url': 'https://i.imgur.com/LITw452.jpg',
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'redacted-at': null,
      fields: {
        video_gamer: {
          type: FieldType.Hash,
          value: {
            online: {
              type: FieldType.Boolean,
              value: true,
            },
            username: {
              type: FieldType.String,
              value: 'jarjarbinks',
            },
            platform: {
              type: FieldType.String,
              value: 'Xbox',
            },
            games: {
              type: FieldType.Array,
              value: [
                {
                  type: FieldType.Hash,
                  value: {
                    name: {
                      type: FieldType.String,
                      value: 'Halo',
                    },
                    level: {
                      type: FieldType.String,
                      value: 'Master Chief',
                    },
                    rank: {
                      type: FieldType.String,
                      value: 'General',
                    },
                    stats: {
                      type: FieldType.Hash,
                      value: {
                        kills: {
                          type: FieldType.Number,
                          value: 1000,
                        },
                      },
                    },
                  },
                },
              ],
            },
          },
        },
        name: {
          type: FieldType.Hash,
          value: {
            first: {
              type: FieldType.String,
              value: 'Jar',
            },
            middle: {
              type: FieldType.String,
              value: 'Jar',
            },
            last: {
              type: FieldType.String,
              value: 'Binks',
            },
          },
        },
        address: {
          type: FieldType.Hash,
          value: {
            street_1: {
              type: FieldType.String,
              value: '353 Sacremento St',
            },
            street_2: {
              type: FieldType.String,
              value: '1400',
            },
            subdivision: {
              type: FieldType.String,
              value: 'CA',
            },
            city: {
              type: FieldType.String,
              value: 'San Francisco',
            },
            postal_code: {
              type: FieldType.String,
              value: '94103',
            },
            country_code: {
              type: FieldType.String,
              value: 'US',
            },
          },
        },
        birthdate: {
          type: FieldType.Date,
          value: '1992-08-19',
        },
        phone_number: {
          type: FieldType.String,
          value: '7037252977',
        },
        email_address: {
          type: FieldType.String,
          value: 'charles@withpersona.com',
        },
        identification_numbers: {
          type: FieldType.Array,
          value: [
            {
              type: FieldType.Hash,
              value: {
                identification_class: {
                  type: FieldType.String,
                  value: 'ssn',
                },
                identification_number: {
                  type: FieldType.String,
                  value: '123551234',
                },
                issuing_country: {
                  type: FieldType.String,
                  value: 'US',
                },
              },
            },
          ],
        },
        selfie_photo: {
          type: FieldType.File,
          value: {
            filename: 'selfie_center.png',
            'byte-size': 100,
            'mime-type': 'image/png',
            url: 'https://i.imgur.com/LITw452.jpg',
          },
        },
      },
      ...attributes,
    },
    relationships: {
      redactor: {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        data: null,
      },
      'account-status': {
        data: {
          type: ApiModel.AccountStatus,
          id: accountStatusId || generateAccountStatusId(),
        },
      },
      'account-type': {
        data: {
          type: ApiModel.AccountType,
          id: accountTypeId || generateAccountTypeId(),
        },
      },
      verifications: {
        data: [],
      },
      'list-matches': {
        data: [],
      },
      ...relationships,
    },
  };
}

export const generateAccountStatusId = () => uniqueId('actst_');
export const generateGraphQueryTemplateId = () => uniqueId('gqt_');

export function createMockAccountStatus(
  name?: string,
  id?: string,
  accountTypeId?: string,
): AccountStatus {
  return {
    id: id || generateAccountStatusId(),
    type: ApiModel.AccountStatus,
    attributes: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'archived-at': null,
      'created-at': '2019-09-20T05:30:12.000Z',
      'updated-at': '2019-09-20T08:30:12.000Z',
      name: name || 'Active',
    },
    relationships: {
      'account-type': {
        data: {
          type: ApiModel.AccountType,
          id: accountTypeId || generateAccountTypeId(),
        },
      },
    },
  };
}

export const generateAccountActionId = () => uniqueId('accac_');

export function createMockAccountAction(
  attributes?: Partial<AccountActionAttributes>,
  relationships?: Partial<AccountActionRelationships>,
  id?: string,
  accountTypeId?: string,
  workflowId?: string,
): AccountAction {
  return {
    id: id || generateAccountActionId(),
    type: ApiModel.AccountAction,
    attributes: {
      'created-at': '2019-09-20T05:30:12.000Z',
      'updated-at': '2019-09-20T08:30:12.000Z',
      name: 'Account Action',
      status: WorkflowStatus.Running,
      ...attributes,
    },
    relationships: {
      'latest-draft-version': {
        data: {
          type: ApiModel.WorkflowVersion,
          id: accountTypeId || generateWorkflowVersionId(),
        },
      },
      workflow: {
        data: {
          type: ApiModel.Workflow,
          id: workflowId || generateWorkflowId(),
        },
      },
      ...relationships,
    },
  };
}

export function createMockAccountEvent(
  attributes?: Partial<AccountEventAttributes>,
  relationships?: Partial<AccountRelationships>,
  id?: string,
): AccountEvent {
  const accountAttributes = omit(attributes, 'tags', 'devices', 'account-status', 'context');
  const mockAccount = createMockAccount(accountAttributes, relationships, id);

  const eventAccountAttributes = pick(attributes, 'tags', 'devices', 'account-status', 'context');

  return {
    attributes: {
      ...mockAccount.attributes,
      ...eventAccountAttributes,
    },
    id: mockAccount.id,
    type: ApiModel.Account,
  };
}
